import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ministryService } from '../../../services/ministry-service';
import {CONTACT_EN} from '../../../models/contact/contact';
import { Ministry } from '../../../models/ministry/ministry';
import { Language } from '../../../constants/language';
import { Asset } from "../../../models/assets/asset";
import { DynamicForm, LocationLink, VideoPlayer, SocialMediaSection, UniversalEnrollButton } from '../../ui';
import { EnrollmentType } from '../../../models/enrollment/enrollment'

const MinistryDetail: React.FC = () => {
  const { ministryId } = useParams<{ ministryId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { i18n } = useTranslation();

  // Helper function to determine if current language is RTL
  const isRTL = i18n.language === 'ar';

  const [ministry, setMinistry] = useState<Ministry | null>(null);

  React.useEffect(() => {
    const fetchMinistries = async () => {
      const result = await ministryService.getMinistries(i18n.language as Language);

      // Update the current ministry based on the new language data
      if (ministryId) {
        const currentMinistry = result.data.find((m) => m.id === Number(ministryId));
        setMinistry(currentMinistry || null);
      }
    };

    fetchMinistries();
  }, [i18n.language, ministryId]);

  // Initialize loading states when ministry loads
  React.useEffect(() => {
    if (ministry) {
      // Initialize image loading states
      if (ministry.media.photos) {
        const imageStates: Record<number, boolean> = {};
        ministry.media.photos.forEach((_, index) => {
          imageStates[index] = true;
        });
        setImageLoadingStates(imageStates);
      }

      // Initialize thumbnail loading states
      if (ministry.media.videos) {
        const thumbnailStates: Record<number, boolean> = {};
        ministry.media.videos.forEach((video, index) => {
          if (video.thumbnailUrl) {
            thumbnailStates[index] = true;
          }
        });
        setThumbnailLoadingStates(thumbnailStates);
      }
    }
  }, [ministry]);

  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [activeVideoIndex, setActiveVideoIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedPdf, setSelectedPdf] = useState<Asset | null>(null);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);
  const [imageLoadingStates, setImageLoadingStates] = useState<Record<number, boolean>>({});
  const [imageErrorStates, setImageErrorStates] = useState<Record<number, boolean>>({});
  const [thumbnailLoadingStates, setThumbnailLoadingStates] = useState<Record<number, boolean>>({});
  const [thumbnailErrorStates, setThumbnailErrorStates] = useState<Record<number, boolean>>({});
  const [pdfLoadingStates, setPdfLoadingStates] = useState<Record<number, boolean>>({});

  const handleEnrollmentSuccess = () => {
    setShowEnrollModal(false);
  };

  const handleEnrollmentCancel = () => {
    setShowEnrollModal(false);
  };

  const handleImageClick = (imageSrc: string) => {
    setSelectedImage(imageSrc);
  };

  const handleVideoChange = (index: number) => {
    setActiveVideoIndex(index);
    setVideoLoading(true);
    setVideoError(false);

    // Initialize thumbnail loading states if not already set
    setThumbnailLoadingStates(prev => {
      const newStates = { ...prev };
      if (!(index in newStates)) {
        newStates[index] = true;
      }
      return newStates;
    });
  };

  const isValidVideoUrl = (url?: string): boolean => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const handleImageLoad = (index: number) => {
    setImageLoadingStates(prev => ({ ...prev, [index]: false }));
  };

  const handleImageError = (index: number) => {
    setImageLoadingStates(prev => ({ ...prev, [index]: false }));
    setImageErrorStates(prev => ({ ...prev, [index]: true }));
  };

  const handleImageStart = (index: number) => {
    setImageLoadingStates(prev => ({ ...prev, [index]: true }));
    setImageErrorStates(prev => ({ ...prev, [index]: false }));
  };

  const handleThumbnailLoad = (index: number) => {
    setThumbnailLoadingStates(prev => ({ ...prev, [index]: false }));
  };

  const handleThumbnailError = (index: number) => {
    setThumbnailLoadingStates(prev => ({ ...prev, [index]: false }));
    setThumbnailErrorStates(prev => ({ ...prev, [index]: true }));
  };

  const handleThumbnailStart = (index: number) => {
    setThumbnailLoadingStates(prev => ({ ...prev, [index]: true }));
    setThumbnailErrorStates(prev => ({ ...prev, [index]: false }));
  };

  const handlePdfClick = (pdf: Asset, index: number) => {
    setPdfLoadingStates(prev => ({ ...prev, [index]: true }));
    setSelectedPdf(pdf);
    // Simulate loading time for PDF viewer
    setTimeout(() => {
      setPdfLoadingStates(prev => ({ ...prev, [index]: false }));
    }, 1000);
  };

  if (!ministryId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Ministry Not Found</h1>
          <button
            onClick={() => navigate('/ministries')}
            className="bg-white text-black hover:bg-gray-100 px-4 py-2 rounded-lg transition-colors">
              {t('pages.ministry-details.buttons.back-button')}
          </button>
        </div>
      </div>
    );
  }

  // Show loading state while ministry data is being fetched
  if (!ministry) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="mb-8">
            <svg className="w-16 h-16 mx-auto animate-spin text-black" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Loading Ministry Details...</h1>
          <p className="text-gray-600 leading-relaxed">Please wait while we fetch the ministry information for you.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
        <div className="absolute inset-0 opacity-10">
          <img
            src={ministry.media.cover?.url || ''}
            alt={ministry.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">{ministry.title}</h1>
            <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto leading-relaxed mb-8">
              {ministry.description}
            </p>
            {/* Hide time and location for Journey (ID 3) and Goodness and Mercy (ID 5) */}
            {ministry.id !== 3 && ministry.id !== 5 && (
              <div className={`flex items-center justify-center ${isRTL ? 'space-x-reverse' : ''} space-x-8 text-sm`}>
                <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>{ministry.time}</span>
                </div>
                <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <LocationLink address={ministry.address} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-12 mb-16 max-w-4xl">

            {/* About Section - Show Vision content when all philosophy attributes exist, otherwise additionalInfo */}
            <section>
              <h2 className="text-3xl font-bold mb-6">{t('pages.ministry-details.about.title')}</h2>
              <div className="prose max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6 whitespace-pre-line">
                  {(ministry.vision && ministry.goal && ministry.mission) ? ministry.vision : ministry.additionalInfo}
                </p>
              </div>
            </section>

            {/* Ministry Philosophy & Vision Section - Main Content when available */}
            {(ministry.quote || ministry.vision || ministry.goal || ministry.mission || ministry.message) && (
              <section className="space-y-8">
                {/* Inspirational Quote */}
                {ministry.quote && (
                  <div className="bg-gradient-to-r from-gray-900 to-black text-white p-8 rounded-2xl">
                    <div className="max-w-4xl">
                      <div className="text-6xl opacity-20 mb-4">"</div>
                      <blockquote className="text-xl md:text-2xl leading-relaxed font-light italic mb-6">
                        {ministry.quote}
                      </blockquote>
                      <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-4`}>
                        <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                          </svg>
                        </div>
                        <div>
                          <div className="font-semibold">
                            <span className="text-white text-shadow-lg font-black text-lg bg-white/20 px-2 py-1 rounded-md shadow-lg border border-white/30">ZOE</span>
                            <span className="ml-2">Ministry</span>
                          </div>
                          <div className="text-sm opacity-75">{ministry.title}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Philosophy Cards - Goal and Mission only (Vision is now main content) */}
                {(ministry.goal || ministry.mission) && (
                  <div className="grid gap-8">
                    {/* Our Goal */}
                    {ministry.goal && (
                      <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-100">
                        <div className="flex items-center mb-6">
                          <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center mr-4">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <h2 className="text-3xl font-bold text-gray-900">
                            {isRTL ? 'هدفنا' : 'Our Goal'}
                          </h2>
                        </div>
                        <div className="text-lg text-gray-700 leading-relaxed whitespace-pre-line">
                          {ministry.goal}
                        </div>
                      </div>
                    )}

                    {/* Our Mission */}
                    {ministry.mission && (
                      <div className="bg-white rounded-lg p-8 shadow-sm border border-gray-100">
                        <div className="flex items-center mb-6">
                          <div className="w-12 h-12 bg-black rounded-lg flex items-center justify-center mr-4">
                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                          </div>
                          <h2 className="text-3xl font-bold text-gray-900">
                            {isRTL ? 'رسالتنا' : 'Our Mission'}
                          </h2>
                        </div>
                        <div className="text-lg text-gray-700 leading-relaxed whitespace-pre-line">
                          {ministry.mission}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Fallback for legacy message field */}
                {!ministry.quote && !ministry.vision && !ministry.goal && !ministry.mission && ministry.message && (
                  <div className="bg-gradient-to-r from-gray-900 to-black text-white p-8 rounded-2xl">
                    <div className="max-w-4xl">
                      <div className="text-6xl opacity-20 mb-4">"</div>
                      <blockquote className="text-xl md:text-2xl leading-relaxed font-light italic mb-6">
                        {ministry.message}
                      </blockquote>
                      <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-4`}>
                        <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                          </svg>
                        </div>
                        <div>
                          <div className="font-semibold">
                            <span className="text-white text-shadow-lg font-black text-lg bg-white/20 px-2 py-1 rounded-md shadow-lg border border-white/30">ZOE</span>
                            <span className="ml-2">Ministry</span>
                          </div>
                          <div className="text-sm opacity-75">{ministry.title}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* Video Section */}
            {ministry.media.videos && ministry.media.videos.length > 0 && isValidVideoUrl(ministry.media.videos[activeVideoIndex]?.url) && (
              <section>
                <h2 className="text-3xl font-bold mb-6">{t('general.video.title')}</h2>
                <div className="bg-black rounded-2xl overflow-hidden mb-4 relative">
                  <div className="aspect-video">
                    {videoLoading && (
                      <div className="absolute inset-0 bg-gray-900 flex items-center justify-center text-white z-10">
                        <div className="flex flex-col items-center space-y-4">
                          <svg className="w-12 h-12 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <div className="text-sm">{t('ui.loading', 'Loading video...')}</div>
                        </div>
                      </div>
                    )}

                    {videoError ? (
                      <div className="w-full h-full bg-gray-900 flex items-center justify-center text-white">
                        <div className="text-center">
                          <div className="w-20 h-20 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                            <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="text-xl mb-2">Error loading video</div>
                          <div className="text-sm text-gray-400 mb-4 max-w-md truncate">
                            {ministry.media.videos[activeVideoIndex]?.url}
                          </div>
                          <div className="space-y-2">
                            <button
                              onClick={() => {
                                setVideoError(false);
                                setVideoLoading(true);
                              }}
                              className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors mr-2"
                            >
                              Retry
                            </button>
                            <a
                              href={ministry.media.videos[activeVideoIndex]?.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 px-4 py-2 rounded-lg transition-colors inline-block"
                            >
                              Open in New Tab
                            </a>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <VideoPlayer
                        key={ministry.media.videos[activeVideoIndex]?.url}
                        url={ministry.media.videos[activeVideoIndex]?.url || ''}
                        className="w-full h-full"
                        poster={ministry.media.videos[activeVideoIndex]?.thumbnailUrl}
                        onLoadStart={() => {
                          setVideoLoading(true);
                          setVideoError(false);
                        }}
                        onCanPlay={() => {
                          setVideoLoading(false);
                        }}
                        onError={() => {
                          setVideoLoading(false);
                          setVideoError(true);
                        }}
                      />
                    )}
                  </div>
                </div>

                {/* Video Gallery Thumbnails */}
                {ministry.media.videos.length > 1 && (
                  <div className={`flex ${isRTL ? 'space-x-reverse' : ''} space-x-3 overflow-x-auto pb-2 mb-4`}>
                    {ministry.media.videos.map((video: Asset, index: number) => (
                      <button
                        key={index}
                        onClick={() => handleVideoChange(index)}
                        className={`flex-shrink-0 relative w-24 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                          activeVideoIndex === index 
                            ? 'border-black shadow-lg' 
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        {/* Video Thumbnail */}
                        {video.thumbnailUrl ? (
                          <div className="relative w-full h-full">
                            {/* Loading placeholder for thumbnail */}
                            {thumbnailLoadingStates[index] && (
                              <div className="absolute inset-0 bg-gray-200 flex items-center justify-center animate-pulse">
                                <svg className="w-4 h-4 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              </div>
                            )}

                            {/* Error placeholder for thumbnail */}
                            {thumbnailErrorStates[index] && (
                              <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              </div>
                            )}

                            <img
                              src={video.thumbnailUrl}
                              alt={`Video ${index + 1} thumbnail`}
                              className={`w-full h-full object-cover transition-opacity duration-300 ${
                                thumbnailLoadingStates[index] ? 'opacity-0' : 'opacity-100'
                              }`}
                              onLoadStart={() => handleThumbnailStart(index)}
                              onLoad={() => handleThumbnailLoad(index)}
                              onError={() => handleThumbnailError(index)}
                            />
                          </div>
                        ) : (
                          <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}

                        {/* Play Icon Overlay */}
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                          <div className={`w-6 h-6 flex items-center justify-center rounded-full ${
                            activeVideoIndex === index ? 'bg-white text-black' : 'bg-white/80 text-black'
                          }`}>
                            <svg className="w-3 h-3 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M8 5v14l11-7z"/>
                            </svg>
                          </div>
                        </div>

                        {/* Active Border */}
                        {activeVideoIndex === index && (
                          <div className="absolute inset-0 border-2 border-black rounded-lg pointer-events-none"></div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Video Counter */}
                {ministry.media.videos.length > 1 && (
                  <div className="text-center mb-4">
                    <p className="text-sm text-gray-600">
                      Video {activeVideoIndex + 1} of {ministry.media.videos.length}
                    </p>
                  </div>
                )}
              </section>
            )}

            {/* Combined Description Section */}
            <section>
              <h2 className="text-3xl font-bold mb-6">{t('pages.ministry-details.ministry-description')}</h2>
              <div className="prose max-w-none bg-white rounded-lg p-8 shadow-sm border border-gray-100">
                <div className="text-lg text-gray-700 leading-relaxed">
                  {/* Combined details */}
                  {ministry.includes && (
                    <div className="mb-6">
                      <p className="font-medium text-xl mb-3">{t('pages.ministry-details.ministry-includes', 'What This Ministry Includes:')}:</p>
                      <p className="mb-4">
                        {ministry.includes.join('. ')}
                      </p>
                    </div>
                  )}

                  {ministry.aspects && (
                    <div className="mb-6">
                      <p className="mb-4">
                        <span className="font-medium">Key aspects include:</span> {ministry.aspects.join('. ')}
                      </p>
                    </div>
                  )}

                  {ministry.tools && (
                    <div className="mb-6">
                      <p className="mb-4">
                        <span className="font-medium">Tools & methods:</span> {ministry.tools.join('. ')}
                      </p>
                    </div>
                  )}

                  {ministry.issues && (
                    <div className="mb-6">
                      <p className="font-medium text-xl mb-3">{t('pages.ministry-details.addresses-issues', 'Addresses Issues Like:')}:</p>
                      <p>
                        {ministry.issues.join('. ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Big Enroll Button - Only show if hasEnrollment is true */}
              {ministry.hasEnrollment && (
                <div className="mt-10 text-center">
                  {ministry.form ? (
                    <UniversalEnrollButton
                      enrollmentUrl={ministry.enrollmentForm}
                      variant="primary"
                      size="lg"
                      className={`${isRTL ? 'flex-row-reverse' : ''} px-10 py-4 text-lg shadow-lg`}
                    >
                      {t('pages.ministry-details.buttons.enroll-button')}
                    </UniversalEnrollButton>
                  ) : (
                    <UniversalEnrollButton
                      enrollmentUrl={ministry.enrollmentForm || `mailto:${CONTACT_EN.EMAIL}?subject=Enrollment Inquiry - ${ministry.title}`}
                      variant="primary"
                      size="lg"
                      className={`${isRTL ? 'flex-row-reverse' : ''} px-10 py-4 text-lg shadow-lg`}
                    >
                      {t('pages.ministry-details.buttons.enroll-button')}
                    </UniversalEnrollButton>
                  )}
                </div>
              )}
            </section>
          </div>

          {/* Right Sidebar */}
          <div className="lg:col-span-1">
            {/* Enrollment Card - Only show if ministry has enrollment */}
            {ministry.hasEnrollment && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 text-center shadow-lg">
                <div className="w-16 h-16 mx-auto mb-4 bg-black text-white rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-4">{t('pages.ministry-details.join.title')}</h3>
                <p className="text-gray-600 mb-6">
                  {t('pages.ministry-details.join.description', { ministry: ministry.title })}
                </p>

                {/* Round Info - Simple */}
                {ministry.hasDuration && ministry.currentRound && (
                  <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-1">{t('pages.ministry-details.round.current')}</p>
                      <p className="font-semibold text-gray-900 mb-3">{t('pages.ministry-details.round.title')} {ministry.currentRound}</p>
                      {ministry.nextRoundStartDate && (
                        <>
                          <p className="text-sm text-gray-600 mb-1">{t('pages.ministry-details.round.next')}</p>
                          <p className="font-medium text-gray-800">{ministry.nextRoundStartDate}</p>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {ministry.form ? (
                  <UniversalEnrollButton
                    enrollmentUrl={ministry.enrollmentForm || ministry.form.externalUrl}
                    variant="primary"
                    size="md"
                    fullWidth={true}
                    className="py-3"
                  >
                    {t('pages.ministry-details.buttons.enroll-button')}
                  </UniversalEnrollButton>
                ) : (
                  <UniversalEnrollButton
                    enrollmentUrl={ministry.enrollmentForm || `mailto:${CONTACT_EN.EMAIL}?subject=Enrollment Inquiry - ${ministry.title}`}
                    variant="primary"
                    size="md"
                    fullWidth={true}
                    className="py-3"
                  >
                    {t('pages.ministry-details.buttons.enroll-button')}
                  </UniversalEnrollButton>
                )}
              </div>
            )}

            {/* Spacing between sidebar components - Only show if enrollment card is visible */}
            {ministry.hasEnrollment && <div className="mb-6"></div>}

            {/* Photo Gallery Carousel */}
            {ministry.media.photos && ministry.media.photos.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4">📸 {t('general.gallery.title')}</h3>
                <div className="relative">
                  {/* Main carousel container */}
                  <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
                    <button
                      onClick={() => handleImageClick(ministry.media.photos[currentImageIndex]?.url || '')}
                      className="w-full h-full flex items-center justify-center hover:opacity-90 transition-opacity group relative overflow-hidden"
                    >
                      {ministry.media.photos[currentImageIndex]?.url ? (
                        <div className="relative w-full h-full">
                          {/* Loading placeholder - show while image is loading */}
                          {imageLoadingStates[currentImageIndex] && (
                            <div className="absolute inset-0 bg-gray-200 flex items-center justify-center animate-pulse">
                              <svg className="w-16 h-16 text-gray-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            </div>
                          )}

                          {/* Error placeholder - show if image failed to load */}
                          {imageErrorStates[currentImageIndex] && (
                            <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
                              <div className="text-center">
                                <svg className="w-16 h-16 text-gray-400 mb-2 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-sm text-gray-500">Failed to load</p>
                              </div>
                            </div>
                          )}

                          <img
                            src={ministry.media.photos[currentImageIndex].url}
                            alt={ministry.media.photos[currentImageIndex].name || `Photo ${currentImageIndex + 1}`}
                            className={`w-full h-full object-cover transition-opacity duration-300 ${
                              imageLoadingStates[currentImageIndex] ? 'opacity-0' : 'opacity-100'
                            }`}
                            onLoadStart={() => handleImageStart(currentImageIndex)}
                            onLoad={() => handleImageLoad(currentImageIndex)}
                            onError={() => handleImageError(currentImageIndex)}
                          />
                        </div>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                          <svg className="w-16 h-16 text-gray-400 group-hover:text-gray-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                      )}
                      {/* Click to expand overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-black px-4 py-2 rounded-lg font-medium">
                          {t('general.gallery.click-to-enlarge')}
                        </div>
                      </div>
                    </button>

                    {/* Navigation arrows */}
                    {ministry.media.photos.length > 1 && (
                      <>
                        <button
                          onClick={() => setCurrentImageIndex(currentImageIndex > 0 ? currentImageIndex - 1 : ministry.media.photos.length - 1)}
                          className={`absolute ${isRTL ? 'right-4' : 'left-4'} top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-black/90 text-white rounded-full flex items-center justify-center transition-colors z-10`}
                        >
                          <svg className={`w-6 h-6 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setCurrentImageIndex(currentImageIndex < ministry.media.photos.length - 1 ? currentImageIndex + 1 : 0)}
                          className={`absolute ${isRTL ? 'left-4' : 'right-4'} top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/70 hover:bg-black/90 text-white rounded-full flex items-center justify-center transition-colors z-10`}
                        >
                          <svg className={`w-6 h-6 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>

                  {/* Thumbnail navigation */}
                  {ministry.media.photos.length > 1 && (
                    <div className={`flex ${isRTL ? 'space-x-reverse' : ''} space-x-2 mt-4 overflow-x-auto pb-2`}>
                      {ministry.media.photos.map((photo: Asset, index: number) => (
                        <button
                          key={index}
                          onClick={() => setCurrentImageIndex(index)}
                          className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                            index === currentImageIndex 
                              ? 'border-black shadow-md' 
                              : 'border-gray-200 hover:border-gray-400'
                          }`}
                        >
                          {photo.url ? (
                            <div className="relative w-full h-full">
                              {/* Loading placeholder for thumbnail */}
                              {imageLoadingStates[index] && (
                                <div className="absolute inset-0 bg-gray-200 flex items-center justify-center animate-pulse">
                                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                </div>
                              )}

                              {/* Error placeholder for thumbnail */}
                              {imageErrorStates[index] && (
                                <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
                                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </div>
                              )}

                              <img
                                src={photo.url}
                                alt={photo.name || `Photo ${index + 1}`}
                                className={`w-full h-full object-cover transition-opacity duration-300 ${
                                  imageLoadingStates[index] ? 'opacity-0' : 'opacity-100'
                                }`}
                                onLoadStart={() => handleImageStart(index)}
                                onLoad={() => handleImageLoad(index)}
                                onError={() => handleImageError(index)}
                              />
                            </div>
                          ) : (
                            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Image counter */}
                  {ministry.media.photos.length > 1 && (
                    <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {currentImageIndex + 1} / {ministry.media.photos.length}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Spacing */}
            {ministry.media.photos && ministry.media.photos.length > 0 && <div className="mb-6"></div>}

            {/* Contact Information */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className={`text-xl font-bold mb-4 flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <span>{t('general.contact.title')}</span>
              </h3>
              <div className="space-y-3">
                <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm">{CONTACT_EN.EMAIL}</span>
                </div>
                <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm">{CONTACT_EN.PHONE}</span>
                </div>
                {/* Hide location for Journey (ID 3) and Goodness and Mercy (ID 5) */}
                {ministry.id !== 3 && ministry.id !== 5 && (
                  <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
                    <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <LocationLink address={ministry.address} className="text-sm" />
                  </div>
                )}
              </div>
            </div>

            {/* Social Media Section */}
            <SocialMediaSection 
              socialMedia={ministry.socialMedia} 
              className="mt-6"
            />

            {/* Spacing */}
            <div className="mb-6"></div>

            {/* PDF Preview Section */}
            {ministry.media.files && ministry.media.files.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-6 mt-6">
                <h3 className="text-xl font-bold mb-4 flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16v-1a4 4 0 014-4h4m-6 5v2a2 2 0 002 2h6a2 2 0 002-2v-6a2 2 0 00-2-2h-2" />
                  </svg>
                  <span>{t('general.pdf.title')}</span>
                </h3>
                <div className="space-y-3">
                  {ministry.media.files.map((pdf: Asset, idx: number) => (
                    <button
                      key={idx}
                      onClick={() => handlePdfClick(pdf, idx)}
                      disabled={pdfLoadingStates[idx]}
                      className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        {pdfLoadingStates[idx] ? (
                          <>
                            <svg className="w-5 h-5 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span className="font-medium text-gray-500 truncate animate-pulse">
                              {t('ui.loading', 'Loading...')}
                            </span>
                          </>
                        ) : (
                          <>
                            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span className="font-medium text-gray-800 truncate">
                              {pdf.name}
                            </span>
                          </>
                        )}
                      </div>
                      <svg className="w-5 h-5 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enrollment Modal */}
      {showEnrollModal && ministry && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden antialiased">
            <div className="overflow-y-auto max-h-[90vh]">
              {/* Header */}
              <div className="relative bg-black text-white p-8">
                <button
                  onClick={() => setShowEnrollModal(false)}
                  className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors group"
                >
                  <svg className="w-6 h-6 text-white group-hover:text-gray-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-6 mb-6`}>
                  {ministry.media.logo && (
                    <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center p-2">
                      <img src={ministry.media.logo?.url || ''} alt={ministry.title} className="w-10 h-10 object-contain" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-3xl font-bold mb-2">Join {ministry.title}</h2>
                    <p className="text-gray-200 text-lg">Begin your journey with us</p>
                  </div>
                </div>

                {/* Duration Info for timed ministries */}
                {ministry.hasDuration && ministry.nextRoundStartDate && (
                  <div className="bg-white/10 border border-white/20 rounded-lg p-4">
                    <h3 className="font-semibold mb-2">Next Round Details</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-300">Duration:</span>
                        <p className="font-medium">{ministry.duration || `Round ${ministry.currentRound || 1}`}</p>
                      </div>
                      <div>
                        <span className="text-gray-300">Start Date:</span>
                        <p className="font-medium">{ministry.nextRoundStartDate}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Dynamic Form Content */}
              <div className="p-8">
                {ministry.form ? (
                  <DynamicForm
                    formConfig={ministry.form}
                    enrollmentType={EnrollmentType.MINISTRY}
                    entityId={ministry.id}
                    onSuccess={handleEnrollmentSuccess}
                    onCancel={handleEnrollmentCancel}
                    isRTL={isRTL}
                  />
                ) : (
                  <div className="text-center py-8">
                    <div className="mb-6">
                      <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">Enrollment Information</h3>
                      <p className="text-gray-600 mb-6">
                        Thank you for your interest in joining {ministry.title}.
                        Please contact us directly for enrollment details and next steps.
                      </p>
                    </div>

                    <div className="space-y-4 max-w-md mx-auto">
                      <div className="flex items-center justify-center space-x-3 text-gray-700">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span>{CONTACT_EN.EMAIL}</span>
                      </div>
                      <div className="flex items-center justify-center space-x-3 text-gray-700">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span>{CONTACT_EN.PHONE}</span>
                      </div>
                    </div>

                    <div className={`flex flex-col sm:flex-row space-y-3 sm:space-y-0 ${isRTL ? 'sm:space-x-reverse' : ''} sm:space-x-4 pt-6 mt-8 border-t border-gray-200`}>
                      <button
                        type="button"
                        onClick={handleEnrollmentCancel}
                        className="flex-1 bg-gray-100 text-gray-800 hover:bg-gray-200 py-4 px-6 rounded-lg transition-colors font-semibold text-lg"
                      >
                        Close
                      </button>
                      <UniversalEnrollButton
                        enrollmentUrl={ministry.enrollmentForm || `mailto:${CONTACT_EN.EMAIL}?subject=Enrollment Inquiry - ${ministry.title}`}
                        variant="primary"
                        size="lg"
                        className="flex-1 py-4 px-6 font-semibold text-lg shadow-lg"
                      >
                        {t('pages.ministry-details.buttons.enroll-button')}
                      </UniversalEnrollButton>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Modal */}
      {selectedPdf && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col">
            {/* Header with close button */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-3`}>
                <svg className="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16v-1a4 4 0 014-4h4m-6 5v2a2 2 0 002 2h6a2 2 0 002-2v-6a2 2 0 00-2-2h-2" />
                </svg>
                <span className="font-bold text-lg">PDF Preview</span>
              </div>
              <button
                onClick={() => setSelectedPdf(null)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* PDF Viewer Container */}
            <div className="flex-1 p-4">
              <div className="w-full h-full bg-gray-100 rounded border">
                <iframe
                  src={selectedPdf?.url || ''}
                  title="PDF Preview"
                  width="100%"
                  height="100%"
                  className="rounded"
                  style={{ border: 'none' }}
                />
              </div>
            </div>

            {/* Footer with action buttons */}
            <div className={`p-4 border-t border-gray-200 flex justify-center ${isRTL ? 'space-x-reverse' : ''} space-x-4`}>
              <a
                href={selectedPdf?.url || ''}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition-colors font-medium"
              >
                Open in New Tab
              </a>
              <a
                href={selectedPdf?.url || ''}
                download
                className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Download PDF
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
          onClick={() => setSelectedImage(null)}
        >
          <div className="max-w-4xl max-h-full p-4">
            <div className="bg-white rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Gallery View</h3>
                <button
                  onClick={() => setSelectedImage(null)}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ×
                </button>
              </div>
              <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                <img
                  src={selectedImage || ''}
                  alt="Enlarged gallery view"
                  className="max-w-full max-h-full object-contain rounded-lg"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MinistryDetail;

/*
 * Loading Placeholders Implementation Summary:
 *
 * This ministry detail page now includes comprehensive loading placeholders similar to
 * product cards, providing better UX during asset loading:
 *
 * 1. **Gallery Images**:
 *    - Main gallery image shows spinner + "Loading image..." text while loading
 *    - Thumbnail images show small spinner placeholders during load
 *    - Error states with appropriate fallback icons and messages
 *
 * 2. **Video Content**:
 *    - Enhanced video loading with consistent spinner design
 *    - Video thumbnail placeholders with loading states
 *    - Proper error handling for failed video loads
 *
 * 3. **PDF Documents**:
 *    - Loading spinners for PDF buttons during interaction
 *    - Animated pulse effects for loading text
 *    - Disabled states during PDF processing
 *
 * 4. **Main Component Loading**:
 *    - Full-page loading state with spinner before ministry data loads
 *    - Consistent loading spinner design across all elements
 *
 * All loading states use:
 * - Consistent spinner design matching ProductCard implementation
 * - Smooth opacity transitions (duration-300)
 * - Proper accessibility with loading text
 * - Error fallbacks with retry options where appropriate
 */
